import { SYSTEM_TID, TagSpecDto } from '@ampsec/platform-client';

export const TAG_SPECS_CATALOG_TTL_MS = 3_600_000;

const CACHE_PREFIX = 'amp.tenant';
const TAG_SPECS_CATALOG_CONTENT_TYPE = 'tagSpecsCatalog';
const CACHE_KEY_PREFIX = `${CACHE_PREFIX}:cache:`;

type TagSpecsCatalogCacheEntry = {
    savedAt: number;
    tid: string;
    specs: TagSpecDto[];
};

const sortObject = (value: Record<string, unknown>) =>
    Object.keys(value)
        .sort()
        .reduce(
            (acc, key) => {
                acc[key] = value[key];
                return acc;
            },
            {} as Record<string, unknown>,
        );

export function getTagSpecsCatalogCacheKey(tid: string): string {
    const descriptor = sortObject({
        'content_type': TAG_SPECS_CATALOG_CONTENT_TYPE,
        'fields.tenantId': tid,
    });
    return `${CACHE_KEY_PREFIX}${JSON.stringify(descriptor)}`;
}

function isTagSpecsCatalogKey(key: string): boolean {
    if (!key.startsWith(CACHE_KEY_PREFIX)) {
        return false;
    }
    try {
        const parsed = JSON.parse(key.slice(CACHE_KEY_PREFIX.length)) as Record<string, unknown>;
        return parsed['content_type'] === TAG_SPECS_CATALOG_CONTENT_TYPE;
    } catch {
        return false;
    }
}

function getStorage(): Storage | null {
    if (typeof window === 'undefined') {
        return null;
    }
    return window.localStorage;
}

function readEntry(tid: string): TagSpecsCatalogCacheEntry | null {
    const storage = getStorage();
    if (!storage) {
        return null;
    }
    const key = getTagSpecsCatalogCacheKey(tid);
    try {
        const raw = storage.getItem(key);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw) as TagSpecsCatalogCacheEntry;
        if (!parsed?.savedAt || !parsed.tid || parsed.tid !== tid || !Array.isArray(parsed.specs)) {
            storage.removeItem(key);
            return null;
        }
        if (Date.now() - parsed.savedAt > TAG_SPECS_CATALOG_TTL_MS) {
            storage.removeItem(key);
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
}

function tenantOnlySpecs(specs: TagSpecDto[], tid: string): TagSpecDto[] {
    return specs.filter((spec) => spec.tid === tid && spec.tid !== SYSTEM_TID);
}

export function readTagSpecsCatalog(tid: string, includeGlobals: boolean): TagSpecDto[] | null {
    const entry = readEntry(tid);
    if (!entry) {
        return null;
    }
    if (includeGlobals) {
        return entry.specs;
    }
    return tenantOnlySpecs(entry.specs, tid);
}

/** Persists the full catalog (tenant + globals). Only writes `includeGlobals: true` payloads. */
export function writeTagSpecsCatalog(tid: string, includeGlobals: boolean, specs: TagSpecDto[]): void {
    if (!includeGlobals) {
        return;
    }
    const storage = getStorage();
    if (!storage) {
        return;
    }
    try {
        const payload: TagSpecsCatalogCacheEntry = {
            savedAt: Date.now(),
            tid,
            specs,
        };
        storage.setItem(getTagSpecsCatalogCacheKey(tid), JSON.stringify(payload));
    } catch {
        // ignore quota / private mode
    }
}

export function clearTagSpecsCatalog(tid?: string): void {
    const storage = getStorage();
    if (!storage) {
        return;
    }
    try {
        if (tid) {
            storage.removeItem(getTagSpecsCatalogCacheKey(tid));
            return;
        }
        const keysToRemove: string[] = [];
        for (let i = 0; i < storage.length; i++) {
            const key = storage.key(i);
            if (key && isTagSpecsCatalogKey(key)) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach((key) => storage.removeItem(key));
    } catch {
        // ignore
    }
}
